import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';

import { Position } from '../../models/position.model';
import {
  defaultFeaturesFor,
  EDIT_ROLES,
  FIELD_ROLES,
  ROUTE_ROLES,
  SEED_VERSION,
  SYSTEM_POSITIONS,
} from '../../guards/route-roles';
import {
  SAVE_SUCCESS,
  SYS_ERROR,
  UPDATE_SUCCESS,
} from '../../../shared/common/message/MessageCommon';

/**
 * CRUD cho collection `position` (vị trí công việc + ma trận quyền).
 *
 * Document ID của vị trí chính là giá trị `users.role`, nên 3 vị trí gốc phải
 * giữ đúng id '1' / '2' / '3' — tài khoản cũ không cần đụng tới.
 */
@Injectable({
  providedIn: 'root',
})
export class PositionService {
  readonly path = 'position';

  constructor(
    private db: AngularFirestore,
    private message: NzMessageService
  ) {}

  /** Toàn bộ vị trí chưa xoá, sắp xếp vị trí gốc lên trước. */
  async getAll(): Promise<Position[]> {
    const snap = await firstValueFrom(
      this.db.collection<Position>(this.path).get()
    );
    const rows = snap.docs.map((d) => ({ ...(d.data() as Position), id: d.id }));
    return rows
      .filter((p) => !p.deleted)
      .sort((a, b) => {
        if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
        return (a.name || '').localeCompare(b.name || '', 'vi');
      });
  }

  async getById(id: string): Promise<Position | null> {
    const doc = await firstValueFrom(
      this.db.collection<Position>(this.path).doc(id).get()
    );
    if (!doc.exists) return null;
    return { ...(doc.data() as Position), id: doc.id };
  }

  /** Tạo vị trí mới (id tự sinh). Trả về id, hoặc chuỗi rỗng nếu lỗi. */
  async create(data: Partial<Position>): Promise<string> {
    const id = this.db.createId();
    try {
      await this.db
        .collection<Position>(this.path)
        .doc(id)
        .set({
          id,
          name: data.name || '',
          code_prefix: data.code_prefix || '',
          view: data.view || [],
          edit: data.edit || [],
          field: data.field || [],
          is_admin: false,
          is_system: false,
          deleted: false,
          created_date: new Date(),
          updated_date: new Date(),
        });
      this.message.success(SAVE_SUCCESS);
      return id;
    } catch (err) {
      console.error('position.create error:', err);
      this.message.error(SYS_ERROR);
      return '';
    }
  }

  async update(id: string, data: Partial<Position>): Promise<boolean> {
    try {
      await this.db
        .collection<Position>(this.path)
        .doc(id)
        .update({
          name: data.name,
          code_prefix: data.code_prefix,
          view: data.view,
          edit: data.edit,
          field: data.field || [],
          updated_date: new Date(),
        });
      this.message.success(UPDATE_SUCCESS);
      return true;
    } catch (err) {
      console.error('position.update error:', err);
      this.message.error(SYS_ERROR);
      return false;
    }
  }

  /** Xoá mềm — giữ đúng quy ước "không bao giờ xoá cứng" của dự án. */
  async softDelete(id: string): Promise<boolean> {
    try {
      await this.db
        .collection<Position>(this.path)
        .doc(id)
        .update({ deleted: true, updated_date: new Date() });
      return true;
    } catch (err) {
      console.error('position.softDelete error:', err);
      this.message.error(SYS_ERROR);
      return false;
    }
  }

  /**
   * Đồng bộ 3 vị trí GỐC với bảng mặc định trong route-roles.ts:
   *
   *  - Chưa có → tạo mới.
   *  - Đã có nhưng `seed_version` cũ hơn SEED_VERSION → ghi đè view/edit/field
   *    (giữ nguyên name/code_prefix mà Admin đã đổi).
   *  - Vị trí do Admin tự tạo (is_system = false) → KHÔNG bao giờ đụng tới.
   *
   * Nhờ vậy sửa bảng quyền trong code là tự áp dụng xuống Firestore ngay lần
   * mở màn hình phân quyền kế tiếp, không phải tick lại bằng tay 23 màn.
   */
  async ensureSeeded(): Promise<void> {
    const snap = await firstValueFrom(
      this.db.collection<Position>(this.path).get()
    );
    const existing = new Map<string, Position>();
    snap.docs.forEach((d) =>
      existing.set(d.id, { ...(d.data() as Position), id: d.id })
    );

    const seeds: Position[] = [
      this.buildSeed(SYSTEM_POSITIONS.ADMIN, 'Admin', 'AD', true),
      this.buildSeed(SYSTEM_POSITIONS.MANAGER, 'Quản lý', 'MA', false),
      this.buildSeed(SYSTEM_POSITIONS.STAFF, 'Nhân viên', 'ST', false),
    ];

    const batch = this.db.firestore.batch();
    let dirty = false;
    for (const seed of seeds) {
      const current = existing.get(seed.id);
      const ref = this.db.collection(this.path).doc(seed.id).ref;

      if (!current) {
        batch.set(ref, seed);
        dirty = true;
        continue;
      }
      if ((current.seed_version || 0) >= SEED_VERSION) continue;

      // Cập nhật quyền theo bảng mới, giữ tên & mã do Admin đặt.
      batch.update(ref, {
        view: seed.view,
        edit: seed.edit,
        field: seed.field,
        is_admin: seed.is_admin,
        is_system: true,
        seed_version: SEED_VERSION,
        updated_date: new Date(),
      });
      dirty = true;
    }
    if (dirty) await batch.commit();
  }

  private buildSeed(
    id: string,
    name: string,
    prefix: string,
    isAdmin: boolean
  ): Position {
    return {
      id,
      name,
      code_prefix: prefix,
      view: defaultFeaturesFor(id, ROUTE_ROLES),
      edit: defaultFeaturesFor(id, EDIT_ROLES),
      field: defaultFeaturesFor(id, FIELD_ROLES),
      is_admin: isAdmin,
      is_system: true,
      seed_version: SEED_VERSION,
      deleted: false,
      created_date: new Date(),
      updated_date: new Date(),
    };
  }
}
